import React, { useContext, useEffect } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link} from "react-router-dom";

import InfoMessageComponent from "@components-shared/InfoMessageComponent";
import { FormInputField } from "@components-shared/desktop/inputs";
import { Button } from "@components/buttons";
import {
	SelectedWithdrawalAmount,
	SelectedWithdrawalPaymentMethod,
	SelectedWithdrawalWallet,
	WithdrawalPasswordConfirmation,
	WithdrawalPaymentSelect,
	WithdrawalWalletSelector,
} from "@features/payment/withdrawal/desktop/new-withdrawal";
import { useRootAppStore } from "@hooks";
import { WithdrawalContext } from "@state/context/payment/WithdrawalContext";
import { getCurrentCulture } from "@utils";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { observer } from "mobx-react-lite";

import WithdrawalTabSwitcher from "../tabs/WithdrawalTabSwitcher";
import WithdrawalActiveBonus from "./WithdrawalActiveBonus";

export default observer(function NewWithdrawalBody() {
	const { userAuthStore } = useRootAppStore();

	const { resetMultiStepFlags, userBonusInfo, paymentMethodError } =
		useContext(WithdrawalContext);

	const { shouldShowDataConfirmationEmptyState } =
		userAuthStore.userDataConfirmationStore;

	const { t } = useTranslation(["payment", "user-account"]);

	useEffect(() => {
		return () => {
			resetMultiStepFlags();
		};
	}, []);

	if (shouldShowDataConfirmationEmptyState) {
		return (
			<div className="at-depositFormMultiPwd card--primary">
				<div className="card--onboarding__content card--onboarding__content--grow">
					<div className="card--primary__header">
						<h1 className="card--primary__title title--primary">
							{t<string>("DEPOSIT.TITLE")}
						</h1>
					</div>
					<WithdrawalTabSwitcher />
					<div className="card--primary__body">
						<div className="card--empty">
							<div className="card--empty__content">
								<i className="u-icon u-icon--xxlrg u-icon--deposit--alt u-mar--bottom--sml"></i>
								<p className="u-mar--bottom--sml">
									{t(
										"user-account:ONLINE_USER_DATA_CONFIRMATION_REQUIRED"
									)}
								</p>
								<div>
									<a
										className="btn btn--med btn--primary--inverted u-mar--right--sml"
										onClick={() =>
											App.state.redirect(
												`/${getCurrentCulture()}/${DefaultPath.HOME}`
											)
										}
									>
										{t("COMMON.BACK_TO_HOME_PAGE")}
									</a>
									<a
										className="btn btn--med btn--primary"
										onClick={
											userAuthStore
												.userDataConfirmationStore
												.redirectToDataConfirmation
										}
									>
										{t("user-account:CONFIRM")}
									</a>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (paymentMethodError && paymentMethodError.errorCode !== 8) {
		return (
			<div className="at-depositFormMultiPwd card--primary">
				<div className="card--primary__header">
					<h1 className="card--primary__title title--primary">
						{t<string>("WITHDRAWAL.TITLE")}
					</h1>
				</div>
				<WithdrawalTabSwitcher />
				<div className="card--primary__body">
					<InfoMessageComponent
						type="warning"
						message={paymentMethodError?.message}
						textClass="message__text"
					/>
				</div>
				<div className="card--primary__footer">
					<FooterButtons errorCode={paymentMethodError?.errorCode} />
				</div>
			</div>
		);
	} else if (userBonusInfo?.isBonusActive) {
		return (
			<div className="at-depositFormMultiPwd card--primary">
				<div className="card--primary__header">
					<h1 className="card--primary__title title--primary">
						{t<string>("WITHDRAWAL.TITLE")}
					</h1>
				</div>
				<WithdrawalTabSwitcher />
				<div className="card--primary__body">
					<WithdrawalActiveBonus
						status={userBonusInfo}
						infoMessage="payment:WITHDRAWAL.ACTIVE_BONUS_WITHDRAWAL"
					/>
				</div>
			</div>
		);
	}

	return <WithdrawalProcess />;
});

function FooterButtons(props: { errorCode: number }) {
	const { errorCode } = props;
	const { t } = useTranslation("account-verification");
	if (errorCode === 18) {
		return (
			<Link
				to={`account-settings/account-verification`}
				className="card--primary__footer__btn btn btn--base btn--accent btn--icon"
			>
				{t("TITLE")}
			</Link>
		);
	} else if (errorCode === 8) {
		return (
			<Link
				to={`deposit`}
				className="card--primary__footer__btn btn btn--base btn--accent btn--icon"
			>
				{t("DEPOSIT.TITLE")}
			</Link>
		);
	}

	return (
		<Link
			to={`info/contact`}
			className="card--primary__footer__btn btn btn--base btn--accent btn--icon"
		>
			{t("FOOTER.CONTACT")}
		</Link>
	);
}

const WithdrawalProcess = observer(function WithdrawalProcess() {
	const { t } = useTranslation("payment");

	const {
		withdrawalResult,
		paymentMethods,
		onlineWallet,
		finalizeWithdrawal,
		initializeWithdrawalProcess,
		isMultiStepWithdrawal,
		resetMultiStepFlags,
		isRedirectingToPaymentPage,
		isLoadingProcess,
		shouldShowWalletSelectors,
		gamesWallet,
	} = useContext(WithdrawalContext);

	const methods = useForm({
		defaultValues: {
			bettingAccountTypeId:
				onlineWallet?.accountTypeId || gamesWallet?.accountTypeId,
			paymentMethodId: "",
			amount: "",
		},
	});

	useEffect(() => {
		methods.reset();
	}, [withdrawalResult]);

	const { handleSubmit, getValues } = methods;

	const onSubmit = (data: any) => {
		initializeWithdrawalProcess(data);
	};

	const onMultiStepSubmit = (data: any) => {
		finalizeWithdrawal(data);
	};

	if (isMultiStepWithdrawal) {
		return (
			<FormProvider {...methods}>
				<form
					className="at-depositFormMultiPwd card--primary"
					onSubmit={handleSubmit(onMultiStepSubmit)}
				>
					<div className="card--primary__header">
						<h1 className="card--primary__title title--primary">
							{t<string>("WITHDRAWAL.TITLE")}
						</h1>
					</div>

					<WithdrawalTabSwitcher />

					<div className="card--primary__body">
						<div className="form__section">
							{shouldShowWalletSelectors && (
								<div className="form__group">
									<SelectedWithdrawalWallet />
								</div>
							)}
							<div className="form__group">
								<SelectedWithdrawalPaymentMethod />
							</div>
							<div className="form__group">
								<SelectedWithdrawalAmount />
							</div>
						</div>
						<div className="form__section">
							<WithdrawalPasswordConfirmation />
						</div>
					</div>

					<div className="card--primary__footer">
						<button
							className="card--primary__footer__btn btn btn--base btn--accent btn--icon"
							type="submit"
							disabled={
								isLoadingProcess || isRedirectingToPaymentPage
							}
						>
							<span className="btn__label">
								{t<string>("COMMON.NEXT_BUTTON")}
							</span>
							<i className="u-icon u-icon--tny u-icon--arrow-right u-color--accent--text" />
						</button>
						<button
							className="card--primary__footer__btn btn btn--base btn--primary--inverted"
							onClick={resetMultiStepFlags}
						>
							<span className="btn__label">
								{t<string>("COMMON.BACK")}
							</span>
						</button>
					</div>
				</form>
			</FormProvider>
		);
	}

	return (
		<FormProvider {...methods}>
			<form
				id="newWithdrawalForm"
				className="card--primary"
				onSubmit={handleSubmit(onSubmit)}
			>
				<div className="card--primary__header">
					<h1 className="card--primary__title title--primary">
						{t<string>("WITHDRAWAL.TITLE")}
					</h1>
				</div>

				<WithdrawalTabSwitcher />

				<div className="card--primary__body">
					{shouldShowWalletSelectors ? (
						<div className="form__group">
							<div className="label">
								{t("COMMON.MY_WALLETS")}
							</div>
							<WithdrawalWalletSelector />
						</div>
					) : (
						<WithdrawalWalletSelector />
					)}

					<div className="form__group">
						<WithdrawalPaymentSelect />
					</div>

					<FormInputField
						required
						label={"payment:WITHDRAWAL.WITHDRAWAL_MONEY"}
						name="amount"
						className="form__group"
						type="text"
						maxLength={12}
						placeholder="payment:COMMON.AMOUNT_PLACEHOLDER"
						pattern={{
							value: /^\d+(\.\d{1,2})?$/,
							message: t(
								"CASINO.BALANCE_TRANSFER.AMOUNT_VALIDATION_ERROR"
							),
						}}
						validate={{
							isInRange: (value: number) => {
								//get payment method object from payment method id
								const currentChosenPaymentMethodId =
									getValues("paymentMethodId");
								const paymentMethodObject =
									paymentMethods?.find(
										(paymentMethod: {
											paymentMethodId: string;
										}) =>
											paymentMethod.paymentMethodId ===
											currentChosenPaymentMethodId
									);
								//get min and max payout (withdrawal) amount from payment method object
								const minPayoutAmount =
									paymentMethodObject?.minPayout!;
								const maxPayoutAmount =
									paymentMethodObject?.maxPayout!;

								return (
									(value >= minPayoutAmount &&
										value <= maxPayoutAmount) ||
									t<string>(
										"WITHDRAWAL.WITHDRAWAL_MONEY_NOT_IN_RANGE",
										{
											0: minPayoutAmount,
											1: maxPayoutAmount,
										}
									)
								);
							},
						}}
					/>
				</div>

				<div className="card--primary__footer">
					<Button
						className="card--primary__footer__btn btn btn--base btn--accent btn--icon"
						type="submit"
						disabled={
							isLoadingProcess ||
							isRedirectingToPaymentPage ||
							paymentMethods == null ||
							paymentMethods.length == 0
						}
						btnText={t<string>("COMMON.CONTINUE_BTN")}
						iconClassName="u-icon u-icon--tny u-icon--arrow-right u-color--accent--text"
					></Button>
				</div>
			</form>
		</FormProvider>
	);
});
